import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { TickIcon } from 'components';
import { formatMoney } from 'src/utils/format';
import { prefersReducedMotion } from 'src/utils/motion';

import style from './HowItWorks.module.scss';

const TUBES = [{ level: '14%', to: '76%', low: true }, { level: '64%' }, { level: '46%' }];

const SEGMENTS = [
  { color: '#9BF0C9', len: 37, off: 0, delay: '.2s', share: '38%' },
  { color: '#645BE8', len: 25, off: 38, delay: '.55s', share: '26%' },
  { color: '#AAA4FF', len: 20, off: 64, delay: '.85s', share: '21%' },
  { color: '#4FB394', len: 14, off: 85, delay: '1.1s', share: '15%' }
];

// Counts from → to after a delay while the scene is playing
const Counter = ({ from, to, decimals = 0, delay = 0, active }) => {
  const { i18n } = useTranslation();
  const [value, setValue] = useState(to);

  useEffect(() => {
    if (!active || prefersReducedMotion) {
      setValue(to);
      return;
    }

    setValue(from);
    let raf = 0;
    const timer = setTimeout(() => {
      const t0 = performance.now();
      const tick = (now) => {
        const k = Math.min(1, (now - t0) / 1300);
        setValue(from + (to - from) * (1 - Math.pow(1 - k, 3)));
        if (k < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [active]);

  return formatMoney(value, i18n.language, decimals);
};

export const ScenePrices = ({ c, active }) => (
  <>
    <span className={style.cap}>{c.caption}</span>
    <div className={style.ripples}>
      <i />
      <i />
      <i />
    </div>
    {c.tags.map(([supplier, price], i) => (
      <span
        key={supplier}
        className={cn(style.tag, style[`t${i + 1}`])}
      >
        {supplier}
        <b>{price}</b>
      </span>
    ))}
    <div className={style.price}>
      <s>{c.was}</s>
      <strong>
        <Counter
          from={42.9}
          to={36.4}
          decimals={2}
          delay={1100}
          active={active}
        />
      </strong>
      <span className={style.who}>{c.who}</span>
    </div>
    <span className={style.stamp}>−15%</span>
  </>
);

export const SceneOrders = ({ c }) => (
  <>
    <span className={style.cap}>{c.caption}</span>
    <div className={style.olist}>
      {c.rows.map(([item, quantity], r) => (
        <div
          key={item}
          className={style.orow}
          style={{ '--r': r }}
        >
          <span className={style.ck}>
            <TickIcon />
          </span>
          <span>{item}</span>
          <b>{quantity}</b>
        </div>
      ))}
      <div className={style.obtn}>
        <span>{c.approve}</span>
        <span className={style.b2}>{c.sent}</span>
      </div>
    </div>
    <div className={style.burst}>
      {Array.from({ length: 12 }, (_, i) => (
        <i
          key={i}
          style={{ '--a': `${i * 30}deg` }}
        />
      ))}
    </div>
  </>
);

export const SceneStock = ({ c }) => (
  <>
    <span className={style.cap}>{c.caption}</span>
    <span className={style.exp}>
      {c.expiryItem}
      <br />
      {c.expiryDate}
    </span>
    <div className={style.tubes}>
      {TUBES.map((tube, i) => (
        <div
          key={i}
          className={cn(style.tube, tube.low && style.low)}
          style={{ '--lv': tube.level, '--to': tube.to }}
        >
          <div className={style.vial}>
            <div className={style.liq}>
              <i className={style.surface} />
            </div>
            <span className={style.minl} />
          </div>
          <span className={style.tl}>{c.tubes[i]}</span>
        </div>
      ))}
    </div>
    <span className={cn(style.mchip, style.warn)}>{c.warn}</span>
    <span className={cn(style.mchip, style.ok)}>{c.ok}</span>
  </>
);

export const SceneAnalytics = ({ c, active }) => (
  <>
    <span className={style.cap}>{c.caption}</span>
    <div className={style.ana}>
      <div className={style.donut}>
        <svg viewBox='0 0 120 120'>
          <circle
            className={style.trk}
            cx='60'
            cy='60'
            r='46'
            pathLength='100'
          />
          {SEGMENTS.map((s) => (
            <circle
              key={s.color}
              className={style.seg}
              cx='60'
              cy='60'
              r='46'
              pathLength='100'
              style={{ '--c': s.color, '--len': s.len, '--off': s.off, '--d': s.delay }}
            />
          ))}
        </svg>
        <div className={style.dc}>
          <strong>
            <Counter
              from={0}
              to={41870}
              delay={200}
              active={active}
            />
          </strong>
          <span>{c.delta}</span>
        </div>
      </div>
      <ul className={style.legend}>
        {c.legend.map((name, r) => (
          <li
            key={name}
            style={{ '--c': SEGMENTS[r].color, '--r': r }}
          >
            {name}
            <b>{SEGMENTS[r].share}</b>
          </li>
        ))}
      </ul>
    </div>
    <span className={style.sparkLabel}>{c.spark}</span>
    <svg
      className={style.spark}
      viewBox='0 0 100 30'
      preserveAspectRatio='none'
    >
      <path
        pathLength='100'
        d='M0,12 L16,9 L33,15 L50,8 L66,13 L83,18 L100,22'
      />
    </svg>
  </>
);
